/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import {
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  FileDoneOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import {
  Col,
  Table,
  Popconfirm,
  Tooltip,
  Row,
  Button,
  Select,
  DatePicker,
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import fileDownload from 'js-file-download';

import { startCase } from 'lodash';
import styles from '../styles';
import {
  errorNotification,
  errorNotificationOnBlob,
  successNotification,
} from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import { canAuthorizeRPO } from './rpoAuthorizers';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  clearRenukaPOs,
  getRenukaPOs,
  getCustomerIdsForRenukaPO,
  getVendorIdsForRenukaPO,
  getBPOIdsForRenukaPO,
  getRPONumbersForRenukaPO,
  getProcessesForRenukaPO,
  clearCustomerIdsForRenukaPO,
  clearVendorIdsForRenukaPO,
  clearBPOIdsForRenukaPO,
  clearRPONumbersForRenukaPO,
  clearProcessesForRenukaPO,
} from '../Actions';
import { currentFinancialYear, financialYears } from '../financial-years';

const { Option } = Select;

const RenukaPOList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const userId = useMemo(() => get(auth, ['user', 'id']), null);
  const renukaPOs = useSelector(state => state.renukaPOs);
  const customerIdsForRenukaPO = useSelector(
    state => state.customerIdsForRenukaPO
  );
  const vendorIdsForRenukaPO = useSelector(state => state.vendorIdsForRenukaPO);
  const bpoIdsForRenukaPO = useSelector(state => state.bpoIdsForRenukaPO);
  const rpoNumbersForRenukaPO = useSelector(
    state => state.rpoNumbersForRenukaPO
  );
  const processesForRenukaPO = useSelector(state => state.processesForRenukaPO);

  const [rowKeys, setRowKeys] = useState([]);
  const [Year, SetYear] = useState(currentFinancialYear);
  const [customerId, setCustomerId] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [bpoId, setBpoId] = useState(null);
  const [rpoId, setRpoId] = useState(null);
  const [process, setProcess] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  useEffect(() => {
    dispatch(getRenukaPOs({ year: Year }));
    dispatch(getCustomerIdsForRenukaPO());
    // dispatch(getVendorIdsForRenukaPO());
    // dispatch(getBPOIdsForRenukaPO());
    // dispatch(getRPONumbersForRenukaPO());
    dispatch(getProcessesForRenukaPO());

    return () => {
      dispatch(clearCustomerIdsForRenukaPO());
      dispatch(clearVendorIdsForRenukaPO());
      dispatch(clearBPOIdsForRenukaPO());
      dispatch(clearRPONumbersForRenukaPO());
      dispatch(clearProcessesForRenukaPO());
      dispatch(clearRenukaPOs());
    };
  }, [Year]);

  const onExport = async () => {
    try {
      const { data } = await Api.getFile(`/purchase-orders/export/excel`, {
        year: Year,
        customerId,
        vendorId,
        bpoId,
        rpoId,
        process,
        from,
        to,
      });
      fileDownload(
        data,
        `Purchase-Orders-${moment().format('DD MMM YYYY')}.xlsx`
      );
    } catch (error) {
      errorNotificationOnBlob(error);
    }
  };

  const onSelectChange = useCallback(
    selectedRowKeys => setRowKeys(selectedRowKeys),
    [setRowKeys]
  );

  const onDelete = async id => {
    try {
      await Api.delete(`/purchase-orders/${id}`);
      successNotification('Purchase order deleted');
      dispatch(getRenukaPOs({ year: Year }));
    } catch (err) {
      errorNotification(err);
    }
  };

  const onFileDownload = async id => {
    try {
      const { data } = await Api.getFile(`/purchase-orders/download/${id}`);
      fileDownload(data, `purchase-orders-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  };

  const downloadColumn = record => (
    <>
      {record.authorizedAt ? (
        <Tooltip placement="bottom" title="Download">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onFileDownload(record.id)}
          />
        </Tooltip>
      ) : null}
    </>
  );

  const onMultipleRPOFileDownload = async () => {
    try {
      const { data } = await Api.getFile(
        `/purchase-orders/download/${rowKeys}`
      );
      fileDownload(data, `purchase-orders.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  };

  const onAuthorize = async id => {
    try {
      await Api.put(`/purchase-orders/${id}/authorize`);
      successNotification('Purchase order authorized');
      dispatch(getRenukaPOs({ year: Year }));
    } catch (err) {
      errorNotification(err);
    }
  };

  const updateColumn = record => (
    <>
      {permissions.includes(constants.permissions.updateRenukaPO) ? (
        <Tooltip placement="bottom" title="Update">
          <Link to={`/renukapo/update/${record.id}`}>
            <EditOutlined className={classes.tableIcon} />
          </Link>
        </Tooltip>
      ) : null}
    </>
  );

  const deleteColumn = record => (
    <>
      {permissions.includes(constants.permissions.deleteRenukaPO) ? (
        <Tooltip placement="bottom" title="Delete">
          <Popconfirm
            title="Are you sure you want to delete?"
            onConfirm={() => onDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined className={classes.tableIcon} />
          </Popconfirm>
        </Tooltip>
      ) : null}
    </>
  );

  const viewRPOColumn = record => (
    <Tooltip placement="bottom" title="View">
      <Link to={`/renukapo/view/${record.id}`}>
        <FileOutlined className={classes.tableIcon} />
      </Link>
    </Tooltip>
  );

  const authorizeColumn = useMemo(
    () => (_text, record) => {
      if (record.authorizeStatus === 'Unauthorized') {
        // HOTFIX 2026-10-29: only Prashant, Ankit and Narender can authorize an RPO (owner's decision).
        // Old rule kept below for revert; to be replaced by an RBAC permission on dev.
        // if (
        //   userId === record.authorizerId ||
        //   userId === 2 ||
        //   userId === 6 ||
        //   userId === 21 ||
        //   userId === 29 ||
        //   userId === 54
        // ) {
        if (canAuthorizeRPO(userId, record.vendorId)) {
          return (
            <Tooltip placement="bottom" title="Authorize">
              <Popconfirm
                title="Are you sure you want to authorize?"
                onConfirm={() => onAuthorize(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <FileDoneOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          );
        }
        return (
          <span style={{ color: '#d9363e' }}>{record.authorizeStatus}</span>
        );
      }
      return record.authorizeStatus;
    },
    []
  );

  const rowTextColor = useMemo(
    () => (text, record) =>
      record.authorizeStatus === 'Unauthorized' ? (
        <span style={{ color: '#d9363e' }}>{text}</span>
      ) : (
        text
      ),
    []
  );

  const rowDateColor = useMemo(
    () => (text, record) =>
      record.authorizeStatus === 'Unauthorized' ? (
        <span style={{ color: '#d9363e' }}>
          {text ? moment(text).format('DD MMM YYYY') : null}
        </span>
      ) : text ? (
        moment(text).format('DD MMM YYYY')
      ) : null,
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Authorize',
        dataIndex: 'authorizeStatus',
        sorter: sorter('authorizeStatus'),
        onFilter: (value, record) =>
          record.authorizeStatus.indexOf(value) === 0,
        filterIcon,
        filters: [
          {
            text: 'Unauthorized',
            value: 'Unauthorized',
          },
          {
            text: 'Authorized',
            value: 'Authorized',
          },
        ],
        render: authorizeColumn,
      },
      {
        title: 'Authorizer',
        dataIndex: 'authorizedBy',
        sorter: sorter('authorizedBy'),
        onFilter: onFilter('authorizedBy'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Category',
        dataIndex: 'category',
        sorter: sorter('category'),
        onFilter: onFilter('category'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Products',
        dataIndex: 'products',
        sorter: sorter('products'),
        onFilter: onFilter('products'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Fabrics',
        dataIndex: 'fabrics',
        sorter: sorter('fabrics'),
        onFilter: onFilter('fabrics'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Styles',
        dataIndex: 'styles',
        sorter: sorter('styles'),
        onFilter: onFilter('styles'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Items',
        dataIndex: 'items',
        sorter: sorter('items'),
        onFilter: onFilter('items'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Accessories',
        dataIndex: 'accessories',
        sorter: sorter('accessories'),
        onFilter: onFilter('accessories'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Grand Total',
        dataIndex: 'grandTotal',
        sorter: sorter('grandTotal'),
        onFilter: onFilter('grandTotal'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
    ];

    if (permissions.includes(constants.permissions.updateRenukaPO)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteRenukaPO)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Version',
        dataIndex: 'versions',
        sorter: sorter('versions'),
        onFilter: onFilter('versions'),
        filterDropdown,
        filterIcon,
        render: rowTextColor,
      },
      {
        title: 'Authorized Date',
        dataIndex: 'authorizedAt',
        sorter: (a, b) =>
          new Date(a.authorizedAt).getTime() -
          new Date(b.authorizedAt).getTime(),
        // sorter: dateSorter('authorizedAt'),
        onFilter: onDateFilter('authorizedAt'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: rowDateColor,
      },
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
            render: rowTextColor,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: rowDateColor,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
            render: rowTextColor,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: rowDateColor,
          },
        ],
      }
    );

    column.push(
      {
        title: 'View',
        render: viewRPOColumn,
        width: '2%',
        fixed: 'right',
      },
      {
        title: 'Download',
        render: downloadColumn,
        width: '3%',
        fixed: 'right',
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 3000, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Purchase Order"
        moduleLink="/renukapo/list"
        subModuleName="List"
      />
      {rowKeys.length ? (
        <>
          <Row className={classes.paddingToContainerContent}>
            <Button
              icon={<DownloadOutlined />}
              onClick={onMultipleRPOFileDownload}
            >
              Download
            </Button>
          </Row>
          <br />
        </>
      ) : null}
      <div className={classes.tableStyle}>
        <Row className={classes.paddingToContainerContent}>
          <Col className={classes.marginBetweenSelect}>
            <div>Financial Year</div>
            <Select
              value={Year}
              style={{ width: 150 }}
              onChange={value => SetYear(value)}
            >
              {financialYears.map(el => (
                <Select.Option key={el} value={el}>
                  {el}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>Customer</div>
            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="Select customer"
              optionFilterProp="children"
              onChange={value => {
                setCustomerId(value);
                dispatch(
                  getVendorIdsForRenukaPO({
                    customerId: value,
                    vendorId,
                    bpoId,
                    rpoId,
                  })
                );
                dispatch(
                  getBPOIdsForRenukaPO({
                    customerId: value,
                    vendorId,
                    bpoId,
                    rpoId,
                  })
                );
                dispatch(
                  getRPONumbersForRenukaPO({
                    customerId: value,
                    vendorId,
                    bpoId,
                    rpoId,
                    process,
                  })
                );
                dispatch(
                  getProcessesForRenukaPO({
                    customerId: value,
                    vendorId,
                    bpoId,
                    rpoId,
                    process,
                  })
                );
              }}
            >
              {customerIdsForRenukaPO.map(customer => (
                <Option value={customer.id} key={customer.id}>
                  {customer.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>Vendor</div>
            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="Select vendor"
              optionFilterProp="children"
              onChange={value => {
                setVendorId(value);
                dispatch(
                  getCustomerIdsForRenukaPO({
                    customerId,
                    vendorId: value,
                    bpoId,
                    rpoId,
                  })
                );
                dispatch(
                  getBPOIdsForRenukaPO({
                    customerId,
                    vendorId: value,
                    bpoId,
                    rpoId,
                  })
                );
                dispatch(
                  getRPONumbersForRenukaPO({
                    customerId,
                    vendorId: value,
                    bpoId,
                    rpoId,
                    process,
                  })
                );
                dispatch(
                  getProcessesForRenukaPO({
                    customerId,
                    vendorId: value,
                    bpoId,
                    rpoId,
                    process,
                  })
                );
              }}
            >
              {vendorIdsForRenukaPO.map(vendor => (
                <Option value={vendor.id} key={vendor.id}>
                  {vendor.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>RPO #</div>
            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="Select RPO #"
              optionFilterProp="children"
              onChange={value => {
                setRpoId(value);
                dispatch(
                  getCustomerIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId: value,
                  })
                );
                dispatch(
                  getVendorIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId: value,
                  })
                );
                dispatch(
                  getBPOIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId: value,
                    process,
                  })
                );
                dispatch(
                  getProcessesForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId: value,
                    process,
                  })
                );
              }}
            >
              {rpoNumbersForRenukaPO.map(rpo => (
                <Option value={rpo.id} key={rpo.id}>
                  {rpo.rpoNo}
                </Option>
              ))}
            </Select>
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>BPO #</div>
            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="Select BPO #"
              optionFilterProp="children"
              onChange={value => {
                setBpoId(value);
                dispatch(
                  getCustomerIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId: value,
                    rpoId,
                  })
                );
                dispatch(
                  getVendorIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId: value,
                    rpoId,
                  })
                );
                dispatch(
                  getRPONumbersForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId: value,
                    rpoId,
                    process,
                  })
                );
                dispatch(
                  getProcessesForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId: value,
                    rpoId,
                    process,
                  })
                );
              }}
            >
              {bpoIdsForRenukaPO.map(bpo => (
                <Option value={bpo.id} key={bpo.id}>
                  {bpo.bpoNo}
                </Option>
              ))}
            </Select>
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>Process</div>
            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="Select Process"
              optionFilterProp="children"
              onChange={value => {
                setProcess(value);
                dispatch(
                  getCustomerIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId,
                    process: value,
                  })
                );
                dispatch(
                  getVendorIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId,
                    process: value,
                  })
                );
                dispatch(
                  getBPOIdsForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId,
                    process: value,
                  })
                );
                dispatch(
                  getRPONumbersForRenukaPO({
                    customerId,
                    vendorId,
                    bpoId,
                    rpoId,
                    process: value,
                  })
                );
              }}
            >
              {processesForRenukaPO.map(text => (
                <Option value={text.process} key={text.process}>
                  {startCase(text.process)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>From </div>
            <DatePicker
              placeholder="From Date"
              onChange={value => setFrom(value ? value.toISOString() : null)}
            />
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>To</div>
            <DatePicker
              placeholder="To Date"
              onChange={value => setTo(value ? value.toISOString() : null)}
            />
          </Col>
          <Col className={classes.marginBetweenSelect}>
            <div>Export</div>
            <Button icon={<FileExcelOutlined />} onClick={onExport}>
              Export
            </Button>
          </Col>
        </Row>
        <br />
        <Table
          bordered
          rowSelection={{
            rowKeys,
            onChange: onSelectChange,
          }}
          size="small"
          columns={columns}
          dataSource={renukaPOs}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

RenukaPOList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(RenukaPOList);
